import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Zap, Users, Star } from 'lucide-react';
import { testimonials } from '../utils/mockData';
import VendorCarousel from '../components/VendorCarousel';
import ITEnvironments from '../components/ITEnvironments';

const Home = () => {
  const services = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Network Architecture',
      description: 'Custom-designed office networks built for security, scalability, and performance.',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'IT Infrastructure',
      description: 'Complete infrastructure setup from servers to endpoints, optimized for your business.',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Ongoing Support',
      description: '24/7 system administration and end-user support to keep your business running.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-blue-100 text-[#2995FF] rounded-full text-sm font-semibold">
                Professional IT Services
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Let CyberTek Design, Secure & Support Your
                <span className="bg-gradient-to-r from-[#2995FF] to-[#004AAD] bg-clip-text text-transparent"> Office Network</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Focus on growing your business while we handle your IT infrastructure, network architecture, and ongoing support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/request-quote"
                  className="px-8 py-4 bg-[#2995FF] text-white rounded-lg font-semibold hover:bg-[#004AAD] shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>Request a Quote</span>
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/services"
                  className="px-8 py-4 bg-white text-[#2995FF] border-2 border-[#2995FF] rounded-lg font-semibold hover:bg-blue-50 flex items-center justify-center"
                >
                  View Services
                </Link>
              </div>
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-sm text-gray-600">Free Consultation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-sm text-gray-600">24/7 Support</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg"
                alt="IT Infrastructure"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced IT solutions tailored to your business needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-8 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-[#2995FF] mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 text-[#2995FF] font-semibold hover:text-[#004AAD]"
            >
              <span>View All Services</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg"
                alt="IT Professional"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">Why Choose CyberTek?</h2>
              <p className="text-lg text-gray-600">
                We combine advanced technology expertise with personalized service to deliver IT solutions that truly work for your business.
              </p>
              <div className="space-y-4">
                {[
                  'Expert network architecture & implementation',
                  'Affordable solutions for small to mid-sized businesses',
                  'Proven track record of successful deployments',
                  'Ongoing support and system administration',
                  'Microsoft 365 specialists',
                  'Cybersecurity and endpoint protection',
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#2995FF] mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT Environments Section */}
      <ITEnvironments />

      {/* Vendor Carousel */}
      <VendorCarousel />

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by businesses across the United States</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">
                    {testimonial.role}, {testimonial.company}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2995FF] to-[#004AAD]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your IT Infrastructure?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Get a free consultation and discover how CyberTek can optimize your office network and IT systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-quote"
              className="px-8 py-4 bg-white text-[#2995FF] rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
            >
              Get Your Free Quote
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#2995FF]"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
